import { connect } from 'react-redux';
import Draggable from 'react-draggable';
import Helmet from 'react-helmet';
import React from 'react';

import {
  getAlignment,
  getBorder,
  getBorderRadius,
  getColors,
  getDropshadowEnabled,
  getFont,
  getIsMinimized,
  getIsVisible,
  getStage,
  getZIndex,
} from 'app/modules';

import ChatClosed from 'presentation/components/ChatClosed';
import ChatThrottled from 'presentation/components/ChatThrottled';
import CustomStyles from 'presentation/components/CustomStyles';
import MinimizedChatButtonContainer from 'presentation/containers/MinimizedChatButtonContainer';
import OnboardingContainer from 'presentation/containers/OnboardingContainer';
import OngoingChat from 'presentation/components/OngoingChat';
import { STAGES } from 'app/modules/ui';

import styles from 'presentation/components/styles/App.css';
import { getIsDraggable } from '../app/modules';
import DemoSelfServiceContainer from 'presentation/containers/__demo__/DemoSelfServiceContainer';
import SelfService from 'presentation/selfService/SelfService';

const OPEN_BODY_CLASS = 'gladlyChat_open';
const MOBILE_BODY_CLASS = 'gladlyChat_mobile_active';

export const App = ({
  alignment,
  border,
  borderRadius,
  colors,
  dropshadowEnabled,
  font,
  isDraggable,
  isMinimized,
  isVisible,
  stage,
  zIndex,
}) => {
  if (isVisible) {
    applyBodyClassName(isMinimized);
    return isDraggable
      ? renderDraggableApp({
          alignment,
          border,
          borderRadius,
          colors,
          dropshadowEnabled,
          font,
          isMinimized,
          stage,
          zIndex,
        })
      : renderApp({ alignment, border, borderRadius, colors, dropshadowEnabled, font, isMinimized, stage, zIndex });
  }
  return null;
};

const applyBodyClassName = isMinimized => {
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  if (isMinimized) {
    document.body.classList.remove(OPEN_BODY_CLASS);
    document.body.classList.remove(MOBILE_BODY_CLASS);
  } else {
    document.body.classList.add(OPEN_BODY_CLASS);
    if (iOS) {
      document.body.classList.add(MOBILE_BODY_CLASS);
    }
  }
};

const renderStage = (stage, isMinimized) => {
  if (isMinimized) {
    return <MinimizedChatButtonContainer />;
  } else if (stage === STAGES.DEMO_SELF_SERVICE) {
    return <DemoSelfServiceContainer />;
  } else if (stage === STAGES.SELF_SERVICE) {
    return <SelfService />;
  } else if (stage === STAGES.ONBOARDING) {
    return <OnboardingContainer />;
  } else if (stage === STAGES.CHAT) {
    return <OngoingChat />;
  } else if (stage === STAGES.CHAT_CLOSED) {
    return <ChatClosed />;
  } else if (stage === STAGES.CHAT_THROTTLED) {
    return <ChatThrottled />;
  }
};

const mapStateToProps = state => {
  return {
    alignment: getAlignment(state),
    border: getBorder(state),
    borderRadius: getBorderRadius(state),
    colors: getColors(state),
    dropshadowEnabled: getDropshadowEnabled(state),
    font: getFont(state),
    isDraggable: getIsDraggable(state),
    isMinimized: getIsMinimized(state),
    isVisible: getIsVisible(state),
    stage: getStage(state),
    zIndex: getZIndex(state),
  };
};

const renderCustomStyles = ({
  alignment,
  border,
  borderRadius,
  colors,
  dropshadowEnabled,
  font,
  isMinimized,
  zIndex,
}) => {
  return (
    <CustomStyles
      alignment={alignment}
      border={border}
      borderRadius={borderRadius}
      colors={colors}
      dropshadowEnabled={dropshadowEnabled}
      font={font}
      isMinimized={isMinimized}
      zIndex={zIndex}
    />
  );
};

const renderHelmet = isMinimized => {
  let helmet;
  if (isMinimized) {
    helmet = <Helmet />;
  } else {
    helmet = (
      <Helmet>
        <meta
          id="gladlyChat_viewportMeta"
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0, viewport-fit=contain"
        />
      </Helmet>
    );
  }
  return helmet;
};

const renderApp = ({
  alignment,
  border,
  borderRadius,
  colors,
  dropshadowEnabled,
  font,
  isMinimized,
  stage,
  zIndex,
}) => {
  return (
    <div className={styles.App} data-aid="gladlyChatAppContainer">
      {renderCustomStyles({ alignment, border, borderRadius, colors, dropshadowEnabled, font, isMinimized, zIndex })}
      {renderHelmet(isMinimized)}
      {renderStage(stage, isMinimized)}
    </div>
  );
};

const renderDraggableApp = ({
  alignment,
  border,
  borderRadius,
  colors,
  dropshadowEnabled,
  font,
  isMinimized,
  stage,
  zIndex,
}) => {
  return (
    <Draggable handle="#gladlyChatDragHandle">
      {renderApp({
        alignment,
        border,
        borderRadius,
        colors,
        dropshadowEnabled,
        font,
        isMinimized,
        stage,
        zIndex,
      })}
    </Draggable>
  );
};

const AppContainer = connect(mapStateToProps)(App);

export default AppContainer;
